// 섹션 데이터 한 번 조회 + **채널별 조각 선택**.
//
// 탭은 **표시 선택**이지 조회 트리거가 아니다. BE 가 세 채널분을 `byPlatform` 에 담아 한 번에
// 내려주므로(2026-08-18 계약 확장), 탭 전환은 맵에서 고르는 일이 전부다 — 기다림이 없고,
// 블록마다 다른 채널을 동시에 볼 수 있다.
//
// 예전 구조는 섹션이 `platform` 하나를 쥐고 탭마다 재조회했다. 그래서 두 가지가 같이 깨졌다.
//   1) 블록 하나의 탭을 바꾸면 같은 state 를 보던 다른 블록까지 끌려갔다(컨트롤은 둘, 실체는 하나).
//   2) 누를 때마다 응답을 기다려야 했고, 그 사이 화면엔 이전 채널 값이 남았다.
import { useCallback, useEffect, useState } from 'react'
import type { ApiError, Platform } from '../creatorsApi'

export interface SectionError {
  message: string
  status?: number
  transport?: boolean
}

export interface SectionData<T, S> {
  /** 채널과 무관한 공통 부분(채널 목록·매출·Confirmed 등). 아직 안 왔으면 null. */
  data: T | null
  /** 그 채널의 조각. 구버전 BE(byPlatform 미도달)면 undefined → 호출부가 최상위로 폴백한다. */
  slice: (platform: Platform) => S | undefined
  loading: boolean
  error: SectionError | null
  reload: () => void
}

/**
 * @param pick 응답에서 채널별 조각 맵을 꺼내는 함수. 미도달이면 undefined 를 돌려준다.
 */
export function usePlatformData<T, S>(
  creatorId: string,
  fetcher: (creatorId: string, platform?: Platform) => Promise<T>,
  pick: (data: T) => Partial<Record<Platform, S>> | undefined,
): SectionData<T, S> {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<SectionError | null>(null)
  const [epoch, setEpoch] = useState(0)

  useEffect(() => {
    let alive = true
    setLoading(true)
    setError(null)
    // platform 을 안 붙인다 — 세 채널분이 한 번에 오므로 고를 이유가 없다.
    // (BE 는 파라미터가 없으면 최상위 필드를 YouTube 로 채우는데, 화면은 byPlatform 만 읽는다.)
    fetcher(creatorId)
      .then((d) => {
        if (!alive) return
        setData(d)
        setError(null)
      })
      .catch((e: unknown) => {
        if (!alive) return
        const err = e as ApiError
        setData(null)
        setError({
          message: err?.message ?? '데이터를 불러오지 못했습니다.',
          status: err?.status,
          transport: err?.transport,
        })
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
    }
  }, [creatorId, epoch, fetcher])

  const reload = useCallback(() => setEpoch((n) => n + 1), [])

  return {
    data,
    slice: (platform) => (data ? pick(data)?.[platform] : undefined),
    loading,
    error,
    reload,
  }
}
